<template>
  <group
    :config="{...groupConfig, transformerTargetName: configTable.name, draggable}"
    style="cursor: pointer;"
    @mouse:over="$emit('mouse:over', $event)"
    @mouse:out="$emit('mouse:out', $event)"
    @mouse:down="$emit('mouse:down', $event, configTable)"
    @mouse:up="$emit('mouse:up', $event)"
  >
    <draw-image
      v-bind="configTable"
      :transformer-target-name="configTable.name"
      :text="text"
      :text-color="textColor"
      :label-rotation="-configTable.rotation"
      :label-background-color="[undefined, 'grey'].includes(textBackgroundColor) ? '#e1e3e3' : floatingBoxColor"
      use-precise-background
      x="0"
      y="0"
      no-ellipsis
      :draggable="false"
      :image="image"
      :allow-transformer-stretch="false"
    />
    <group :config="{x: 0, y: 0, rotation: 0}">
      <draw-box
        v-bind="configTable"
        :name="`${configTable.name}-bounding-box`"
        :transformer-target-name="configTable.name"
        x="0"
        y="0"
        :draggable="false"
        color="transparent"
      />
      <template v-if="!!configTable.floatingBoxPosition">
        <draw-box
          v-if="!hideStatusBox"
          v-bind="statusBoxConfig"
        >
          <draw-image
            :name="configTable.name"
            :transformer-target-name="configTable.name"
            x="0"
            y="0"
            height="20"
            width="20"
            :image="statusIcon"
            is-static
          />
        </draw-box>
        <draw-box
          v-if="!hideServerBox"
          v-bind="serverBoxConfig"
          :color="showServerAssignment ? 'green' : serverBoxConfig.color"
        />
      </template>
    </group>
  </group>
</template>

<script>
import DrawImage from '@/ft-vue-core/components/canvas/DrawImage'
import DrawBox from '@/ft-vue-core/components/canvas/DrawBox'
import Group from '@/ft-vue-core/components/canvas/Group'
const BOX_PADDING = 4
export default {
  name: 'DrawTable',
  components: {DrawImage, DrawBox, Group},
  props: {
    locationTable: {
      type: Object,
    },
    configTable: {
      type: Object,
      required: true
    },
    image: {
      type: Image,
      required: true
    },
    text: String,
    textColor: String,
    textBackgroundColor: String,
    serverInitial: String,
    serverInitialColor: String,
    serverColor: String,
    serverTextColor: String,
    statusIcon: Image,
    showServerAssignment: Boolean,
    draggable: Boolean,
    hideServerBox: Boolean,
    hideStatusBox: Boolean,
    floatingBoxColor: String,
  },
  data () {
    return {}
  },
  computed: {
    tableRotation () {
      return this.configTable.rotation
    },
    groupConfig () {
      const {name, x, y} = this.configTable
      return {name: `draw-table-${name}`, x, y, rotation: 0}
    },
    boxConfig () {
      const {name, height, width, scaleX, scaleY} = this.configTable
      return {
        name: `table-${name}`,
        baseName: name,
        transformerTargetName: name,
        height: height * scaleY / 3,
        width: width * scaleX / 3,
        cornerRadius: 5,
        color: this.floatingBoxColor || 'grey',
        x: 0,
        y: 0,
        scaleX: 1,
        scaleY: 1,
        labelScale: (scaleX + scaleY) / 2,
        isStatic: true,
      }
    },
    statusBoxConfig () {
      const {height, width, scaleX, scaleY} = this.configTable
      const [_height, _width] = [height / 3, width / 3]
      return Object.assign(
        {},
        this.boxConfig,
        this.getStatusBoxPosition(_width, _height, width, height, scaleX, scaleY)
      )
    },
    serverBoxConfig () {
      const {serverInitial: text, serverInitialColor, serverColor, serverTextColor} = this.$props
      const {height, width, scaleX, scaleY} = this.configTable
      const [_height, _width] = [height / 3, width / 3]
      return Object.assign(
        {text, textColor: serverColor ? serverTextColor || 'white' : serverInitialColor},
        this.boxConfig,
        {color: serverColor || 'grey'},
        this.getServerBoxPosition(_width, _height, width, height, scaleX, scaleY),
      )
    }
  },
  methods: {
    getStatusBoxPosition (width, height, parentWidth, parentHeight, scaleX, scaleY) {
      const {floatingBoxPosition} = this.configTable
      const [_parentWidth, _parentHeight] = [parentWidth * scaleX, parentHeight * scaleY]
      switch (floatingBoxPosition) {
      case 'TOP': return {offsetX: width / -2 * scaleX - BOX_PADDING, offsetY: _parentHeight / 2 + height * scaleY / 6}
      case 'LEFT': return {offsetX: _parentWidth / 2 + width * scaleX / 6, offsetY: height / -2 * scaleY - BOX_PADDING}
      case 'BOTTOM': return {offsetX: width / -2 * scaleX - BOX_PADDING, offsetY: _parentHeight / -2 - height * scaleY / 6}
      case 'RIGHT': return {offsetX: _parentWidth / -2 - width * scaleX / 6, offsetY: height / -2 * scaleY - BOX_PADDING}
      case 'T_B': return {offsetX: 0, offsetY: _parentHeight / -2 - height * scaleY / 6}
      case 'L_R': return {offsetX: _parentWidth / -2 - width * scaleX / 6, offsetY: 0}
      }
      return {offsetX: 0, offsetY: 0}
    },
    getServerBoxPosition (width, height, parentWidth, parentHeight, scaleX, scaleY) {
      const {floatingBoxPosition} = this.configTable
      const [_parentWidth, _parentHeight] = [parentWidth * scaleX, parentHeight * scaleY]
      switch (floatingBoxPosition) {
      case 'TOP': return {offsetX: width / 2 * scaleX + BOX_PADDING, offsetY: _parentHeight / 2 + height * scaleX / 6}
      case 'LEFT': return {offsetX: _parentWidth / 2 + width * scaleX / 6, offsetY: height / 2 * scaleY + BOX_PADDING}
      case 'BOTTOM': return {offsetX: width / 2 * scaleX + BOX_PADDING, offsetY: _parentHeight / -2 - height * scaleY / 6}
      case 'RIGHT': return {offsetX: _parentWidth / -2 - width * scaleX / 6, offsetY: height / 2 * scaleY + BOX_PADDING}
      case 'T_B': return {offsetX: 0, offsetY: _parentHeight / 2 + height * scaleY / 6}
      case 'L_R': return {offsetX: _parentWidth / 2 + width * scaleX / 6, offsetY: 0}
      }
      return {offsetX: 0, offsetY: 0}
    },
    getTextBackgroundSize () {
      //
    }
  }
}
</script>
